import { ReactNode } from "react";
import { Attachment } from "@carbon/icons-react";
import { Tag } from "@carbon/react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import { OfferResponse, SupplierRfqActivity } from "../../types";
import SupplierValidationTag, { activityByEmail } from "./SupplierValidationTag";

/** Same loose-typed `t` convention `orderListRows.tsx`/`materialRows.tsx` use. */
type TFunc = (key: string) => string;

export interface OfferRow extends DataTableRow {
  id: string;
  offer: OfferResponse;
  supplier: ReactNode;
  validation: ReactNode;
  price: ReactNode;
  comment: ReactNode;
  attachment: ReactNode;
  status: ReactNode;
}

/**
 * Replaces `OfferItem.tsx`'s per-row `stateAndCompliance` gate (offer.order.state === OPEN &&
 * isCompliant buyer): while an RFQ is OPEN, a compliant buyer must not see any offered price,
 * comment, attachment indicator, or "awarded" badge on ANY row — only the supplier name. The
 * caller (`OffersOnOrders.tsx`) computes this once from `state`/`isCompliant` and passes it in
 * here rather than this file reaching into `useUser()` itself, keeping the row-mapper a pure
 * function (independently unit-testable — see `offerRows.test.ts`).
 */
export const buildOfferRows = (
  offers: OfferResponse[] | undefined,
  hideOfferDetails: boolean,
  t: TFunc,
  activity?: SupplierRfqActivity[]
): OfferRow[] => {
  const activityOf = activityByEmail(activity);
  if (!offers) return [];

  // Sort by price ascending, same order `OffersOnOrders.tsx` always rendered — copy first so the
  // query cache's own array (React Query's cached `offersOnOrders.content`) is never mutated by
  // `Array.prototype.sort`, unlike the pre-Carbon `offOnOrd?.sort(...)` which sorted in place.
  return [...offers]
    .sort((a, b) => a.price - b.price)
    .map((offer) => ({
      id: offer.id,
      offer,
      supplier: offer.user?.company?.name || "-",
      validation: (
        <SupplierValidationTag
          activity={activityOf.get(offer.user?.email?.toLowerCase() ?? "")}
        />
      ),
      price: hideOfferDetails
        ? null
        : `${offer.price} ${offer.currency} / ${offer.order?.measurementUnit ?? ""}`,
      comment: hideOfferDetails ? null : offer.comment || null,
      attachment:
        !hideOfferDetails && offer.fileURL ? (
          <Attachment aria-label={t("common.viewFile")} />
        ) : null,
      status:
        !hideOfferDetails && offer.selected ? (
          <Tag type="green">{t("offer.success")}</Tag>
        ) : null,
    }));
};

export const offerListHeaders = (t: TFunc): DataTableColumn<OfferRow>[] => [
  { key: "supplier", header: t("tenders.data.suppliers") },
  { key: "validation", header: t("tenders.supplierActivity.validation") },
  { key: "price", header: t("tenders.form.price") },
  { key: "comment", header: t("common.comment") },
  { key: "attachment", header: t("tenders.data.attachment") },
  { key: "status", header: t("tenders.data.offerStatus") },
];
